import { useEffect, useMemo, useState } from 'react';
import {
  CardRecord,
  LocalParagraphScore,
  ParagraphLLMFeedback,
  ParagraphRecallRecord,
  updateCard,
  WritingContent,
} from '../../db/indexedDb';
import ParagraphRecallResult from './ParagraphRecallResult';
import { scoreParagraphRecall } from './localDiff';
import { getSynonymWhitelist } from './synonymWhitelist';
import { evaluateParagraphWithLLM, hasLLMSupport } from '../../services/llm/writing';
import { generateWritingClozeFromRecall } from '../lightReview/cloze';

type ParagraphRecallCardProps = {
  card: CardRecord;
  onLocalScore?: (score: LocalParagraphScore) => void;
  persistResult?: (record: ParagraphRecallRecord) => Promise<void>;
  onRecallRecord?: (record: ParagraphRecallRecord) => void;
  onAnswerChange?: (hasAnswer: boolean) => void; // 输入变化时调用
};

const defaultPersist = async (card: CardRecord, record: ParagraphRecallRecord) => {
  await updateCard(card.id, {
    writingMeta: { ...(card.writingMeta ?? {}), lastRecall: record },
    content: { ...(card.content ?? {}), lastRecallAnswer: record.answer },
  });
};

const deriveKeyUnits = (card: CardRecord): string[] => {
  const content = (card.content ?? {}) as WritingContent;
  const writingMeta = card.writingMeta ?? {};
  // 优先使用 writingMeta.keyPhrases
  const phrases = Array.isArray(writingMeta.keyPhrases) && writingMeta.keyPhrases.length > 0
    ? writingMeta.keyPhrases
    : Array.isArray(content.keyPhrases)
      ? content.keyPhrases
      : [];
  return phrases.filter(Boolean).slice(0, 3);
};

const fallbackCnHint = (card: CardRecord): string => {
  const content = (card.content ?? {}) as WritingContent;
  const subTopicTitle = card.writingMeta?.extraMeta?.subTopicTitle;
  // 优先显示中文总结 prompt
  return (
    content.prompt || 
    (typeof subTopicTitle === 'string' ? subTopicTitle : undefined) ||
    card.writingMeta?.cnHint || 
    '写作提示缺失'
  );
};

const getTopicTag = (card: CardRecord) => {
  const content = (card.content ?? {}) as WritingContent;
  return content.topicTag || content.csvTopic || '';
};

const fallbackReference = (card: CardRecord) => {
  const content = (card.content ?? {}) as WritingContent;
  return card.writingMeta?.enOriginal || content.answer || content.reference || '';
};

function ParagraphRecallCard({ card, onLocalScore, persistResult, onRecallRecord, onAnswerChange }: ParagraphRecallCardProps) {
  const [answer, setAnswerRaw] = useState('');
  const setAnswer = (value: string) => {
    setAnswerRaw(value);
    onAnswerChange?.(value.trim().length > 0);
  };
  const [score, setScore] = useState<LocalParagraphScore | null>(null);
  const [llmFeedback, setLlmFeedback] = useState<ParagraphLLMFeedback | null>(null);
  const [llmState, setLlmState] = useState<'idle' | 'pending' | 'ready' | 'error'>('idle');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submittedAt, setSubmittedAt] = useState<number | null>(null);
  const [showReference, setShowReference] = useState(false);

  const cnHint = useMemo(() => fallbackCnHint(card), [card]);
  const topicTag = useMemo(() => getTopicTag(card), [card]);
  const reference = useMemo(() => fallbackReference(card), [card]);
  const keyUnits = useMemo(() => deriveKeyUnits(card), [card]);
  const offline = typeof navigator !== 'undefined' ? !navigator.onLine : false;
  const synonymGroups = useMemo(() => getSynonymWhitelist(card.writingMeta?.synonymWhitelistId), [card]);

  useEffect(() => {
    // reset when card changes
    setAnswer('');
    setScore(null);
    setLlmFeedback(null);
    setLlmState('idle');
    setProcessing(false);
    setError(null);
    setSubmittedAt(null);
    setShowReference(false);
  }, [card.id]);

  const persist = async (record: ParagraphRecallRecord) =>
    (persistResult ? persistResult(record) : defaultPersist(card, record))
      .then(() => onRecallRecord?.(record))
      .catch((err) => {
        console.error('[paragraph recall] persist failed', err);
        setError('保存本地评分时出错');
      });

  const handleSubmit = async () => {
    if (!reference) {
      setError('缺少标准答案，无法比对');
      return;
    }
    if (!answer.trim()) {
      setError('请输入英文答案再提交');
      return;
    }

    setProcessing(true);
    setError(null);

    const localScore = scoreParagraphRecall({ reference, answer, keyUnits, synonymGroups });
    setScore(localScore);
    setSubmittedAt(Date.now());
    onLocalScore?.(localScore);

    const recordBase: ParagraphRecallRecord = {
      answer,
      evaluatedAt: Date.now(),
      localScore,
      llmFeedback: undefined,
    };

    await persist(recordBase);
    // 生成写作填空卡（一源一卡，LLM 智能挖空）
    generateWritingClozeFromRecall(card, recordBase).catch((err) =>
      console.warn('[paragraph recall] generate cloze skipped', err)
    );

    try {
      if (await hasLLMSupport()) {
        setLlmState('pending');
        const writingContent = (card.content ?? {}) as WritingContent;
        const feedback = await evaluateParagraphWithLLM({
          question: writingContent.prompt,
          reference,
          userAnswer: answer,
        });
        setLlmFeedback(feedback);
        setLlmState('ready');
        await persist({ ...recordBase, llmFeedback: feedback });
      } else {
        setLlmState('idle');
      }
    } catch (err) {
      console.error('[paragraph recall] LLM evaluation failed', err);
      setLlmState('error');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="practice-card writing-compact">
      {/* Compact header */}
      <div className="practice-card-header">
        {topicTag && <span className="practice-topic-tag">{topicTag}</span>}
        <h3 className="practice-question">{cnHint}</h3>
        {keyUnits.length > 0 && (
          <div className="practice-cues">
            {keyUnits.map((phrase) => (
              <span className="pill" key={phrase}>{phrase}</span>
            ))}
          </div>
        )}
      </div>

      {/* Input area */}
      <div className="writing-input-area">
        <div className="writing-input-row">
          <textarea
            id={`paragraph-input-${card.id}`}
            className="writing-textarea"
            rows={3}
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            placeholder="默写英文段落..."
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
          />
          <button className="writing-submit-btn" type="button" disabled={processing} onClick={handleSubmit}>
            {processing ? '...' : '判分'}
          </button>
        </div>
      </div>

      {error && <p className="error-text" style={{ margin: 0, padding: '8px 12px', fontSize: '12px' }}>{error}</p>}

      {/* 判分前显示原文按钮 - 和听力一致 */}
      {!score && (
        <div className="recall-actions">
          <button
            type="button"
            className="session-action neutral"
            onClick={() => setShowReference(!showReference)}
          >
            {showReference ? '隐藏原文' : '显示原文'}
          </button>
        </div>
      )}
      {!score && showReference && (
        <div className="recall-block" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <p className="section-label">原文对照</p>
          <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0', minHeight: 0 }}>
            <p className="session-body">{reference || '文本待加载'}</p>
            {card.writingMeta?.cnTranslation && (
              <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginTop: '12px', padding: '8px', background: 'var(--bg-page)' }}>
                {card.writingMeta.cnTranslation}
              </p>
            )}
          </div>
        </div>
      )}

      {score && (
        <ParagraphRecallResult
          score={score}
          reference={reference}
          llmFeedback={llmFeedback}
          llmState={llmState}
        />
      )}
    </div>
  );
}

export default ParagraphRecallCard;
